import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { ScrollText, LogIn, TrendingUp, Sliders, Edit3, XCircle, Layers } from 'lucide-react';

const ACTION_META = {
  impersonate: { icon: LogIn, color: '#0ea5e9', label: 'Logged in as user' },
  set_trading: { icon: Sliders, color: '#a855f7', label: 'Changed leverage/volatility' },
  edit_position: { icon: Edit3, color: '#eab308', label: 'Edited position' },
  close_position: { icon: XCircle, color: '#ef4444', label: 'Closed position' },
  settle_position: { icon: TrendingUp, color: '#00e676', label: 'Forced P&L (settle)' },
  settle_all: { icon: Layers, color: '#00e676', label: 'Forced ALL trades P&L' },
  set_spread: { icon: Sliders, color: '#f97316', label: 'Updated spread settings' }
};

const fmtMeta = (a) => {
  const m = a.meta || {};
  if (a.action === 'set_trading') return `leverage=${m.leverage ?? '—'}x · volatility=${m.volatility_factor ?? '—'}x`;
  if (a.action === 'settle_position') return `P&L ${m.pnl >= 0 ? '+' : ''}$${Number(m.pnl || 0).toFixed(2)}`;
  if (a.action === 'settle_all') return `${m.count || 0} trade(s) · each ${m.pnl >= 0 ? '+' : ''}$${Number(m.pnl || 0).toFixed(2)}`;
  if (a.action === 'set_spread') return [m.global_pips != null ? `global=${m.global_pips}p` : null, m.overrides ? `${Object.keys(m.overrides).length} override(s)` : null].filter(Boolean).join(' · ');
  if (a.action === 'close_position') return `@ ${m.close_price} · P&L ${m.pnl >= 0 ? '+' : ''}$${Number(m.pnl || 0).toFixed(2)}`;
  if (a.action === 'edit_position') return Object.entries(m).filter(([k]) => k !== 'position_id').map(([k, v]) => `${k}=${v}`).join(' · ');
  return '';
};

const AdminAudit = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => { try { setRows(await adminAPI.auditLog()); } catch {} setLoading(false); })();
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Audit Log</h1>
        <p className="text-sm text-gray-400">Every impersonation, forced trade, and leverage/volatility change — with the admin and timestamp.</p>
      </div>
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center gap-2">
          <ScrollText size={14} className="text-[#00e676]"/>
          <span className="text-sm font-semibold text-white">Activity</span>
          <span className="text-xs text-gray-500">({rows.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Action</th><th>Admin</th><th>Target User</th><th>Details</th><th className="pr-4">When</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan="5" className="py-8 text-center text-gray-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-gray-500">No admin actions logged yet</td></tr>}
              {rows.map(a => {
                const meta = ACTION_META[a.action] || { icon: ScrollText, color: '#8aa39b', label: a.action };
                const Ico = meta.icon;
                return (
                  <tr key={a.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 text-white font-medium">
                        <span className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}><Ico size={13}/></span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="text-gray-300">{a.admin_email || a.admin_id}</td>
                    <td className="text-gray-300">{a.target_email || a.target_user_id || '—'}</td>
                    <td className="text-xs text-gray-400 mono">{fmtMeta(a)}</td>
                    <td className="pr-4 text-xs text-gray-400 mono whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAudit;
